/** @typedef {import('../types.d.ts').MediaManagerSettings} MediaManagerSettings */

import { load as fingerprintLoad } from "@fingerprintjs/botd";
import { validate as uuidValidate } from 'uuid';

let userSearchRequired = false;

/**
 * Check if a URL parameter exists.
 * @param {string} param
 * @returns {string|null}
 */
function getUrlParam(param) {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has(param) ? urlParams.get(param) : null;
}

/**
 * Get a formatted date (YYYY-MM-DDTHH:mm:SS) for the current date + an offset.
 * @param {number} offset
 * @returns {string}
 */
function formatExpiryDate(offset = 0) {
  const currentTime = (new Date()).getTime();
  return new Date(currentTime + offset).toISOString().slice(0, 19);
}

/**
 * Extract the data from an base64 encoded user token.
 * @param {string|null} tokenString Encoded User Token
 * @returns {object|null}
 */
function getUserTokenData(tokenString) {
  if (!tokenString) {
    return null;
  }

  try {
    let tokenJson = atob(tokenString);

    if (tokenJson.length > 0) {
      return JSON.parse(tokenJson);
    }
  }
  catch (error) {
    // Invalid token.
    console.warn("Unable to decode token.");
  }

  return null;
}

/**
 * Generate a unique view UUID.
 * https://stackoverflow.com/questions/105034/how-do-i-create-a-guid-uuid#answer-2117523
 * @returns {string} - view ID
 */
function generateViewId() {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  // Fallback for local development not using https.
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, c =>
    (+c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> +c / 4).toString(16)
  );
}

/**
 * @typedef {object} AnalyticsData
 * @property {object} custom_1    Information to save in analytics system.
 * @property {object} custom_2    Information to save in analytics system.
 */

/**
 * @param {AnalyticsData} data - Raw data
 * @returns {Promise<AnalyticsData>} - data with replaced values
 */
async function replaceAnalyticsTokens(data) {
  // Get user key, UUID and person token from drupalSettings.
  let userKey = drupalSettings.media_manager.user_key;
  let userUuid = drupalSettings.media_manager.user_uuid;
  let personToken = drupalSettings.media_manager.person_token;
  let userEmail = null;

  const userToken = getUrlParam("UT");
  const tokenData = getUserTokenData(userToken);
  const urlPersonToken = getUrlParam("PT");

  if (tokenData) {
    userKey = tokenData.user_key;
    userUuid = tokenData.guid || "Anonymous";
    userEmail = tokenData.email;
  }

  if (urlPersonToken && uuidValidate(urlPersonToken)) {
    personToken = urlPersonToken;
  }

  // Get view rule ID.
  let viewRuleId = await getVideoRuleId(userUuid);

  // Check for custom dimensions.
  for (let i = 1; i <= drupalSettings.media_manager.dimension_limit; i++) {
    let value = data[`custom_${i}`];

    if (typeof value === "undefined") {
      continue;
    }

    const newValue = await replaceToken(value, userUuid, personToken, userToken, userKey, userEmail, viewRuleId);
    data[`custom_${i}`] = newValue.replaceAll("&quot;", '"');
  }
  return { ...data };
}

/**
 * 
 * @returns {string} - video view ID
 */
function getVideoViewId() {
  const videoId = drupalSettings.phoenix_media_player.media_content_id;
  const userId = drupalSettings.user.uid;
  let viewId = sessionStorage.getItem(`media-manager-view-id:${userId}:${videoId}`);

  if (viewId === null) {
    viewId = generateViewId();
    sessionStorage.setItem(`media-manager-view-id:${userId}:${videoId}`, viewId);
  }

  return viewId;
}

/**
 * 
 * @param {string} userUuid - the user ID to retrieve the rule for
 * @returns {Promise<string | null>} - the resolved rule ID
 */
async function getVideoRuleId(userUuid) {
  let contentId;

  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has('plx')) {
    contentId = `playlist:${urlParams.get('plx')}-${userUuid}`;
  }

  if (!contentId && drupalSettings.phoenix_media_player?.media_content_id) {
    contentId = `node:${drupalSettings.phoenix_media_player.media_content_id}-${userUuid}`;
  }

  const siteId = drupalSettings.phoenix_media_player?.site_player?.id;

  if (siteId) {
    contentId = `${contentId}-${siteId}`
  }

  try {
    const response = await fetch(`/api/view-rule/${contentId}`);
    const data = await response.json();
    return data?.view_rule_id || null;
  } catch (error) {
    console.error("Failed to fetch view rule ID:", error);
    return null;
  }
}

/**
 * @param {string} value - Raw value, potentially containing string tokens.
 * @param {string} userUuid - The user UUID to use for replacement.
 * @param {string} personToken - The person token to use for replacement.
 * @param {string} userToken - The user token to use for replacement.
 * @param {string} userKey - The user key to use for replacement.
 * @param {string} userEmail - The user email to use for replacement.
 * @param {string} viewRuleId - The view rule ID to use for replacement.
 * @returns {Promise<string>} - tokens
 */
async function replaceToken(value, userUuid, personToken, userToken, userKey, userEmail, viewRuleId) {
  const offsets = {
    "24hr": 24 * 3600 * 1000,
    "30day": 30 * 24 * 3600 * 1000,
    "2years": 730 * 24 * 3600 * 1000,
  };

  let tokens = {
    "user:id": drupalSettings.user.uid,
    "user:uuid": userUuid,
    "user:person_token": personToken,
    "user:token": userToken,
    "user:user_key": userKey || "",
    "user:video_view_id": getVideoViewId(),
    "expiry:24hr": formatExpiryDate(offsets["24hr"]),
    "expiry:30day": formatExpiryDate(offsets["30day"]),
    "expiry:2years": formatExpiryDate(offsets["2years"]),
    "view:rule_id": viewRuleId,
  };

  for (const token in tokens) {
    if (
      (token === "user:uuid" && userEmail && (userUuid === "Anonymous" || !userUuid)) ||
      (token === "user:person_token" && !personToken)
    ) {
      // Do this later to prevent delay.
      userSearchRequired = true;
    }

    value = value.replace(new RegExp(`\\\[custom:${token}\\\]`, "g"), tokens[token]);
  }
  return value;
}

/**
 * Search for a user guid to replace "Anonymous" in the data sent to Mux.
 * @param {object} settings drupalSettings object.
 * @param {object} embed Media Manager embed object.
 * @param {any | null} [muxCustomDataKey] - custom key
 * @returns {Promise<void>} - who knows
 */
async function runUserSearch(settings, embed, muxCustomDataKey = null) {
  if (!userSearchRequired) {
    return;
  }

  // We already know that the UT is valid and has an email address but no
  // uuid. Send request to try and get the uuid.
  if (!embed.control().mux) {
    setTimeout(() => runUserSearch(settings, embed), 1000);
    return;
  }
  const userToken = getUrlParam("UT");

  if (!userToken) {
    return;
  }
  let guidData;

  try {
    let response = await fetch(`/api/media-manager/find-user/${userToken}`);

    guidData = await response.json();

    if (!guidData.guid) {
      throw new Error('No guid in payload')
    }
  } catch (error) {
    console.error("Failed to fetch user guid", error);
    return;
  }


  try {
    let newData = {};
    let shouldEmitData = false;

    for (let i = 1; i <= settings.media_manager.dimension_limit; i++) {
      let value = settings.media_manager.analytics[`custom_${i}`];

      try {
        const customData = JSON.parse(value);

        if (customData.user_id === "Anonymous") {
          customData.user_id = guidData.guid;
          newData[`custom_${i}`] = settings.media_manager.analytics[`custom_${i}`] = JSON.stringify(customData);
          shouldEmitData = true;
        }

        if ("person_token" in customData && guidData.person_token) {
          customData.person_token = guidData.person_token;
          newData[`custom_${i}`] = settings.media_manager.analytics[`custom_${i}`] = JSON.stringify(customData);
          shouldEmitData = true;
        }
      }
      catch {
        continue;
      }
    }

    if (shouldEmitData) {
      if (muxCustomDataKey) {
        // If mux was initialised using MUX SDK then need to pass through the
        // player ID as the first parameter.
        embed.control().mux.emit(muxCustomDataKey, "hb", newData);
      }
      else {
        // Using JWPlayer integration.
        embed.control().mux.emit("hb", newData);
      }

      drupalSettings?.elastic_tracker?.track.forEach(element => {
        // Overwrite the elastic_tracker guid.
        element.guid = guidData.guid;
      });
    }
  }
  catch (error) {
    console.warn("Failed to resend custom parameters", error);
  }
}

/**
 *
 * @param {MediaManagerSettings} mediaManagerSettings - settings object
 * @param {string | null} [muxCustomDataKey] - custom key from Mux
 * @returns {Promise<{} | null>} - updated analytics
 */
async function replaceUserGuid(mediaManagerSettings, muxCustomDataKey = null) {
  if (!userSearchRequired) {
    return null;
  }
  const userToken = getUrlParam("UT");

  if (!userToken) {
    return null;
  }
  let data;

  try {
    let response = await fetch(`/api/media-manager/find-user/${userToken}`);

    data = await response.json();
    if (!data.guid) {
      throw new Error('no guid found in payload');
    }
  } catch (error) {
    console.error("Failed to fetch user guid", error);
    return { ...mediaManagerSettings.analytics };
  }

  let updatedAnalytics = { ...mediaManagerSettings.analytics };

  for (let i = 1; i <= mediaManagerSettings.dimension_limit; i++) {
    let value = updatedAnalytics[`custom_${i}`];

    try {
      const customData = JSON.parse(value);

      if (customData.user_id === "Anonymous") {
        customData.user_id = data.guid;
        updatedAnalytics[`custom_${i}`] = JSON.stringify(customData);
      }

      if ("person_token" in customData && data.person_token) {
        customData.person_token = data.person_token;
        updatedAnalytics[`custom_${i}`] = JSON.stringify(customData);
      }
    }
    catch {
      continue;
    }
  }

  return updatedAnalytics;
}

/**
 * Determine if the anonymous user looks like a bot.
 * @returns {boolean} True if user looks like a bot.
 */
async function isSuspectedBot() {
  try {
    const botd = await fingerprintLoad();

    const result = await botd.detect();

    return result.bot;
  }
  catch (error) {
    console.error(`Failed to run bot check: ${error}`);
  }

  return false;
}

export {
  getUrlParam,
  runUserSearch,
  replaceAnalyticsTokens,
  replaceUserGuid,
  isSuspectedBot,
};
